import React, { useMemo, useState } from 'react';

import pluralize from 'pluralize';

import { AutoAwesome } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { usePopulateFromProperty } from '~/hooks/apis/offers/usePopulateFromProperty';

const CARD_SX = { flex: 1, minWidth: 110, p: 1.5, border: 1, borderColor: 'grey.200', borderRadius: 2 } as const;

interface UploadResultRow {
  row: number;
  status: 'Failed' | 'Skipped' | 'Queued';
  id_salesforce_external: string;
  offerId: string;
  propertyId?: string;
  message: string;
}

interface UploadResult {
  total: number;
  failed: number;
  skipped: number;
  rows: Array<UploadResultRow>;
}

interface AutoPopulateResultRow {
  offerId: string;
  propertyId: string;
  status: 'Succeeded' | 'Failed';
  message: string;
}

interface BulkAutoPopulateResponseRow {
  offerIdSalesforceExternal: string;
  propertyId: string;
  status: number;
  message: string;
}

const STATUS_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  Queued: 'success',
  Succeeded: 'success',
  Failed: 'error',
  Skipped: 'warning',
};

const columns: Array<GridColDef> = [
  { field: 'row', headerName: 'Row', width: 70 },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    renderCell: (params) => <Chip label={params.value} color={STATUS_COLOR[params.value] ?? 'default'} size="small" />,
  },
  { field: 'id_salesforce_external', headerName: 'Vendor Id', flex: 1, minWidth: 140 },
  { field: 'offerId', headerName: 'Offer Id', flex: 1, minWidth: 160 },
  { field: 'message', headerName: 'Message', flex: 1.5, minWidth: 200 },
];

const autoPopulateColumns: Array<GridColDef> = [
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    renderCell: (params) => <Chip label={params.value} color={STATUS_COLOR[params.value] ?? 'default'} size="small" />,
  },
  { field: 'offerId', headerName: 'Offer Id', flex: 1, minWidth: 160 },
  { field: 'propertyId', headerName: 'Property Id', flex: 1, minWidth: 160 },
  { field: 'message', headerName: 'Message', flex: 1.5, minWidth: 200 },
];

const AUTO_POPULATE_BATCH_SIZE = 10;

async function autoPopulateInBatches(
  rows: Array<{ offerId: string; propertyId?: string }>,
  mutateAsync: (
    offers: Array<{ offerIdSalesforceExternal: string; propertyId: string }>,
  ) => Promise<{ result: Array<BulkAutoPopulateResponseRow> }>,
): Promise<Array<AutoPopulateResultRow>> {
  const results: Array<AutoPopulateResultRow> = [];
  for (let i = 0; i < rows.length; i += AUTO_POPULATE_BATCH_SIZE) {
    const batch = rows.slice(i, i + AUTO_POPULATE_BATCH_SIZE);
    try {
      const response = await mutateAsync(
        batch.map((row) => ({ offerIdSalesforceExternal: row.offerId, propertyId: row.propertyId })),
      );
      results.push(
        ...response.result.map((row) => ({
          offerId: row.offerIdSalesforceExternal,
          propertyId: row.propertyId,
          status: row.status === 200 ? ('Succeeded' as const) : ('Failed' as const),
          message: row.message,
        })),
      );
    } catch (error) {
      results.push(
        ...batch.map((row) => ({
          offerId: row.offerId,
          propertyId: row.propertyId,
          status: 'Failed' as const,
          message: error?.message || 'unexpected error',
        })),
      );
    }
  }
  return results;
}

interface Props {
  result: UploadResult;
  onAutoPopulatingChange?: (isAutoPopulating: boolean) => void;
}

export default function PropertiesUploadResult({ result, onAutoPopulatingChange }: Props) {
  const rows = useMemo(() => (result.rows ?? []).map((row) => ({ id: row.row, ...row })), [result.rows]);
  const queuedRows = useMemo(() => (result.rows ?? []).filter((row) => row.status === 'Queued'), [result.rows]);
  const queued = queuedRows.length;

  const autoPopulateMutation = usePopulateFromProperty();
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  const [autoPopulateResults, setAutoPopulateResults] = useState<Array<AutoPopulateResultRow> | null>(null);
  const [activeTab, setActiveTab] = useState<'rows' | 'autoPopulate'>('rows');

  const autoPopulateRows = useMemo(
    () => (autoPopulateResults ?? []).map((row, index) => ({ id: index, ...row })),
    [autoPopulateResults],
  );
  const autoPopulateFailedRows = useMemo(
    () => autoPopulateRows.filter((row) => row.status === 'Failed'),
    [autoPopulateRows],
  );

  const runAutoPopulate = async (rowsToRun: Array<{ offerId: string; propertyId?: string }>) => {
    setIsAutoPopulating(true);
    onAutoPopulatingChange?.(true);
    try {
      const results = await autoPopulateInBatches(rowsToRun, autoPopulateMutation.mutateAsync);
      setAutoPopulateResults((previous) => {
        const retriedKeys = new Set(results.map((row) => `${row.offerId}:${row.propertyId}`));
        const kept = (previous ?? []).filter((row) => !retriedKeys.has(`${row.offerId}:${row.propertyId}`));
        return [...kept, ...results];
      });
      setActiveTab('autoPopulate');
    } finally {
      setIsAutoPopulating(false);
      onAutoPopulatingChange?.(false);
    }
  };

  const handleAutoPopulate = () => runAutoPopulate(queuedRows);
  const handleRetryFailed = () =>
    runAutoPopulate(autoPopulateFailedRows.map((row) => ({ offerId: row.offerId, propertyId: row.propertyId })));

  return (
    <Stack spacing={2.5}>
      {queuedRows.length > 0 && !autoPopulateResults && (
        <Paper
          elevation={0}
          sx={{ p: 2, border: 1, borderColor: 'primary.main', borderRadius: 2, bgcolor: 'action.hover' }}
        >
          <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
              <AutoAwesome color="primary" fontSize="small" sx={{ mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                Auto-populates each offer&apos;s slug, location, fine print, booking guarantee, AI generated description
                and getting there, and brand/region schedules from its property record.
              </Typography>
            </Stack>
            <Button
              variant="contained"
              onClick={handleAutoPopulate}
              disabled={isAutoPopulating}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {isAutoPopulating && <CircularProgress size={20} sx={{ mr: 1 }} />}
              {isAutoPopulating
                ? 'Auto-populating offers...'
                : `Auto-populate ${pluralize('offer', queuedRows.length, true)}`}
            </Button>
          </Stack>
        </Paper>
      )}

      <Box>
        {autoPopulateResults && (
          <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)} sx={{ mb: 1.5 }}>
            <Tab value="rows" label={`Upload rows (${rows.length})`} />
            <Tab value="autoPopulate" label={`Auto-populate results (${autoPopulateResults.length})`} />
          </Tabs>
        )}

        {(!autoPopulateResults || activeTab === 'rows') && (
          <Box>
            <Stack direction="row" spacing={2} mb={1.5} flexWrap="wrap">
              <Paper elevation={0} sx={CARD_SX}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Total
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {result.total}
                </Typography>
              </Paper>
              <Paper elevation={0} sx={CARD_SX}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Queued
                </Typography>
                <Typography variant="h5" fontWeight={600} color={queued > 0 ? 'success.main' : 'text.disabled'}>
                  {queued}
                </Typography>
              </Paper>
              <Paper elevation={0} sx={CARD_SX}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Failed
                </Typography>
                <Typography variant="h5" fontWeight={600} color={result.failed > 0 ? 'error.main' : 'text.disabled'}>
                  {result.failed}
                </Typography>
              </Paper>
              <Paper elevation={0} sx={CARD_SX}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Skipped
                </Typography>
                <Typography variant="h5" fontWeight={600} color={result.skipped > 0 ? 'warning.main' : 'text.disabled'}>
                  {result.skipped}
                </Typography>
              </Paper>
            </Stack>
            <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableColumnMenu
                autoHeight
                sx={{ border: 0 }}
              />
            </Paper>
          </Box>
        )}

        {autoPopulateResults && activeTab === 'autoPopulate' && (
          <Box>
            <Stack direction="row" spacing={2} flexWrap="wrap" mb={1.5}>
              <Paper elevation={0} sx={CARD_SX}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Total
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {autoPopulateResults.length}
                </Typography>
              </Paper>
              <Paper elevation={0} sx={CARD_SX}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Succeeded
                </Typography>
                <Typography variant="h5" fontWeight={600} color="success.main">
                  {autoPopulateResults.length - autoPopulateFailedRows.length}
                </Typography>
              </Paper>
              <Paper elevation={0} sx={CARD_SX}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Failed
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color={autoPopulateFailedRows.length > 0 ? 'error.main' : 'text.disabled'}
                >
                  {autoPopulateFailedRows.length}
                </Typography>
              </Paper>
            </Stack>

            {autoPopulateFailedRows.length > 0 && (
              <Box mb={1.5} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleRetryFailed}
                  disabled={isAutoPopulating}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {isAutoPopulating && <CircularProgress size={20} sx={{ mr: 1 }} />}
                  {isAutoPopulating
                    ? `Auto-populating ${pluralize('offer', autoPopulateFailedRows.length, true)}...`
                    : `Retry ${pluralize('failed offer', autoPopulateFailedRows.length, true)}`}
                </Button>
              </Box>
            )}
            <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
              <DataGrid
                rows={autoPopulateRows}
                columns={autoPopulateColumns}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableColumnMenu
                autoHeight
                sx={{ border: 0 }}
              />
            </Paper>
          </Box>
        )}
      </Box>
    </Stack>
  );
}
